<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
                Overview
            </h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">
                To grow your impact, your organization may choose to invest in new projects or initiatives, such as expanding into new areas, establishing a new facility, or introducing new services. Use this section <strong>only if you plan to pursue new projects</strong> over the next three years. This module will guide you in clearly articulating what these new efforts are, what resources they will require, and why they matter.
                <template v-if="isModuleSkipped">
                    <br />
                    <div class="mt-[22px]">
                        <span class="mt-2 text-[#00423D] font-semibold text-[16px] leading-[20px] cursor-pointer">This Module is Skipped</span>
                        <a @click.prevent="handleUnskip" data-action="unskip" class="inline-flex items-center underline ml-2 text-[#00423D] font-semibold text-[20px] leading-[22px] cursor-pointer">
                            Unskip it 
                            <img class="ml-1 w-[18px] h-[16px] align-text-bottom" src="@/assets/arrow-right-clr.svg" alt="" />
                        </a>
                    </div>
                </template>
                <template v-else>
                    <br />
                    <div class="mt-[22px]">
                        <span class="mt-2 text-[#00423D] font-semibold text-[16px] leading-[20px]">This Module is Optional</span>
                        <a @click.prevent="handleSkip" data-action="skip" class="inline-flex items-center underline ml-2 text-[#00423D] font-semibold text-[20px] leading-[22px] cursor-pointer">
                            Skip it 
                        <img class="ml-1 w-[18px] h-[16px] align-text-bottom" src="@/assets/arrow-right-clr.svg" alt="" />
                        </a>
                    </div>
                </template>
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStepValidation } from '@/composables/useStepValidation';
import { useSkippedModulesStore } from '@/stores/skippedModules';
// Use the validation composable with default settings
useStepValidation();

const router = useRouter();
const route = useRoute();
const skippedModulesStore = useSkippedModulesStore();

const isModuleSkipped = computed(() => {
    // Check if the skipped modules store is initialized
    if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
        return false;
    }
    
    // Extract the current module path from the route (just the module path, not including section)
    const pathParts = route.path.split('/');
    if (pathParts.length < 3) {
        return false;
    }
    
    // Get just the module path (first two parts after the leading slash)
    const modulePath = `/${pathParts[1]}/${pathParts[2]}`;
    
    // For module 9, always show as skipped if it's in the skipped routes
    if (modulePath === '/steps/funding-new-projects') {
        return skippedModulesStore.skippedRoutes.includes(modulePath);
    }
    
    // Check if this module path is in the skipped routes array
    return skippedModulesStore.skippedRoutes.includes(modulePath);
});

const handleSkip = async () => {
    try {    
        // Get the current module path from the route
        const currentPath = route.path;
        const pathParts = currentPath.split('/');
        
        if (pathParts.length < 3) {
            console.error('Invalid route path format for skipping');
            return;
        }
        
        // Extract the module path (only the first 3 parts of the path)
        const currentModulePath = `/${pathParts[1]}/${pathParts[2]}`;
        
        // Initialize the routes to skip array with the current module
        let routesToSkip = [currentModulePath];
        

        
        // Clean up any previous unskip operations to avoid conflicts
        localStorage.removeItem('unskippedModules');
        localStorage.removeItem('unskipTimestamp');
        
        // Set flags to force refresh on page load
        localStorage.setItem('forceRefreshSkippedModules', 'true');
        localStorage.setItem('forcedSkip', 'true');
        localStorage.setItem('skipTimestamp', Date.now().toString());
        
        // Force a fresh fetch first to make sure we have latest data
        await skippedModulesStore.fetchSkippedModules(true);
        
        // Update skipped modules in the API (will merge with existing skipped modules)
        const updatedSkippedRoutes = await skippedModulesStore.updateSkippedModules(routesToSkip);
            
        // Verify skip was successful by checking if all routesToSkip are in the updated skipped routes
        const skipSuccessful = routesToSkip.every(route => 
            Array.isArray(updatedSkippedRoutes) && updatedSkippedRoutes.includes(route)
        );
        
        if (!skipSuccessful) {      
            // Force update the local state if API didn't respond correctly
            const existingRoutes = [...skippedModulesStore.skippedRoutes];
            routesToSkip.forEach(route => {
                if (!existingRoutes.includes(route)) {
                    existingRoutes.push(route);
                }
            });
            
            skippedModulesStore.skippedRoutes = existingRoutes;
            
            localStorage.setItem('skipped_modules', JSON.stringify({
                skippedRoutes: existingRoutes,
                timestamp: Date.now()
            }));
        }
        
        // Determine which module to redirect to - always go to module 10
        const targetModule = '/steps/total-fundraising-goal/summary';
        
        // Clear the pending redirect since we'll use Vue Router
        localStorage.removeItem('pendingRedirect');
        
        // Dispatch an event so other components can react
        window.dispatchEvent(new CustomEvent('skipped-modules-updated', {
            detail: {
                skippedRoutes: skippedModulesStore.skippedRoutes,
                operation: 'skip',
                timestamp: Date.now()
            }
        }));
        
        // Force a final sync of local state before navigation
        localStorage.setItem('skipped_modules', JSON.stringify({
            skippedRoutes: skippedModulesStore.skippedRoutes,
            timestamp: Date.now()
        }));
        
        // Add delay before redirect to ensure state has propagated
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Use Vue Router navigation instead of window.location to avoid page refresh
        router.push(targetModule);
    } catch (error) {
        console.error('Error skipping modules:', error);
        // Fallback redirect in case of error
        router.push('/steps/total-fundraising-goal/summary');
    }
};

const isUnskipLoading = ref(false);

async function handleUnskip() {
    // Get the current module path
    const currentPathParts = route.path.split('/');
    let currentModulePath = '';
    
    if (currentPathParts.length >= 3) {
        currentModulePath = `/${currentPathParts[1]}/${currentPathParts[2]}`;
    } else {
        console.error('Unable to unskip module: Invalid module path');
        return;
    }
    
    // Set loading state
    isUnskipLoading.value = true;
    
    try {
        // Clean up any previous skip operations to avoid conflicts
        localStorage.removeItem('forcedSkip');
        localStorage.removeItem('skipTimestamp');
        localStorage.removeItem('pendingRedirect');
        
        // Force refresh skipped modules first to ensure we have latest data
        await skippedModulesStore.fetchSkippedModules(true);
        
        // Store which specific module is being unskipped for other components to check
        let unskippedModules = [];
        
        // Determine which modules will be unskipped
        unskippedModules.push(currentModulePath);
        

        
        // Set localStorage values for unskip operation
        localStorage.setItem('unskippedModules', JSON.stringify(unskippedModules));
        localStorage.setItem('unskipTimestamp', Date.now().toString());
        localStorage.setItem('forceRefreshSkippedModules', 'true');
        localStorage.setItem('unskipOperation', 'true');
        
        // Filter out the modules to unskip from the current skipped modules
        const filteredSkippedRoutes = skippedModulesStore.skippedRoutes.filter(
            route => !unskippedModules.includes(route)
        );
        
        // Update the skipped modules in the API
        const updatedSkippedRoutes = await skippedModulesStore.updateSkippedModules(filteredSkippedRoutes);
            
        // Verify the modules were actually unskipped
        const stillSkipped = unskippedModules.some(path => 
            skippedModulesStore.skippedRoutes.includes(path)
        );
        
        if (stillSkipped) {
            
            // Apply a more aggressive override by forcing modules to be removed
            const forcedUpdatedRoutes = skippedModulesStore.skippedRoutes.filter(
                route => !unskippedModules.includes(route)
            );
            
            skippedModulesStore.skippedRoutes = forcedUpdatedRoutes;
            
            // Force persist to localStorage
            localStorage.setItem('skipped_modules', JSON.stringify({
                skippedRoutes: forcedUpdatedRoutes,
                timestamp: Date.now()
            }));
        }
        
        // Dispatch an event so other components can react
        window.dispatchEvent(new CustomEvent('module-unskipped', {
            detail: {
                unskippedModules,
                timestamp: Date.now()
            }
        }));
        
        // Add delay before refreshing the view
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Use Vue Router to refresh the current view without a full page reload
        router.replace({
            path: route.path,
            query: { _t: Date.now() } // Add timestamp to force view refresh
        });
    } catch (error) {
        console.error('Error unskipping module:', error);
    } finally {
        isUnskipLoading.value = false;
    }
}
</script>

<style scoped>
</style> 